import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, Inject, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Discount, DiscountService, ListDiscountsRequestFiltersInterface } from '@galaxy/billing';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, publishReplay, reduce, refCount, switchMap } from 'rxjs/operators';
import { FormatDiscountPipe } from '../pipes/format-discount.pipe';
import { Pricing, PricingMessageContainer } from './interface';
import { getPricingMessageContainers } from './utils';

@Component({
  standalone: true,
  imports: [NgIf, NgClass, NgForOf, MatIconModule, GalaxyPopoverModule, TranslateModule, AsyncPipe, FormatDiscountPipe],
  selector: 'app-activation-table-pricing',
  templateUrl: './table-pricing.component.html',
  styleUrls: ['./table-pricing.component.scss'],
})
export class TablePricingComponent {
  containerLength = 0;
  _pricing: Pricing;

  pricingMessageContainers: PricingMessageContainer;
  isProratedToParent: boolean;
  showPricingPopover = false;
  discounts$: Observable<Discount[]>;

  @Input() billingId: string;

  /* Only compute messages once whenever pricing is set.
   * A setter was chosen because it ensures the necessary computation only happens once.
   * If a function was used, it would be called on every change detection cycle unnecessarily.
   */
  @Input()
  set pricing(p: Pricing) {
    this._pricing = p;
    if (p) {
      this.isProratedToParent = p.isProratedToParent;
    }
    this.pricingMessageContainers = getPricingMessageContainers(p);
    this.containerLength = this.pricingMessageContainers?.pricingMessages
      ? this.pricingMessageContainers.pricingMessages.length
      : 0;
  }

  get pricing(): Pricing {
    return this._pricing;
  }

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private discountService: DiscountService,
  ) {}

  private fetchAllDiscounts(pid: string, sku: string): Observable<Discount[]> {
    const pageSize = 50;
    const filters: ListDiscountsRequestFiltersInterface = {
      merchantId: pid,
      expiry: new Date(),
      skus: [sku],
    };

    return this.discountService.list(filters, '', pageSize).pipe(
      expand((resp) => {
        if ((resp?.results || []).length >= pageSize) {
          return this.discountService.list(filters, resp.nextCursor, pageSize);
        } else {
          return EMPTY;
        }
      }),
      reduce((prev, curr) => {
        const discounts = curr?.results || [];
        return prev.concat(discounts);
      }, [] as Discount[]),
    );
  }

  private getDiscounts(): Observable<Discount[]> {
    return this.partnerId$.pipe(
      switchMap((pid) => {
        return this.fetchAllDiscounts(pid, this.billingId);
      }),
      catchError(() => of([])),
      publishReplay(1),
      refCount(),
    );
  }

  itemInfoIconEnter(): void {
    if (this.discounts$ === undefined) {
      this.discounts$ = this.getDiscounts();
    }
    this.showPricingPopover = true;
  }

  itemInfoIconLeave(): void {
    this.showPricingPopover = false;
  }
}
